import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    ArrowRight, Sparkles, Users, BookOpen, Handshake, Rocket,
    Compass, Lightbulb, Landmark, Quote, ExternalLink, Linkedin,
} from "lucide-react";
import SEO from "@/components/SEO";
import WaitlistForm from "@/components/WaitlistForm";
import { ASSETS, LINKEDIN_URL, api } from "@/lib/api";

const PILLARS = [
    { icon: Lightbulb, title: "Learn", d: "Stories, insights and lessons from founders building businesses that matter." },
    { icon: Users, title: "Connect", d: "A curated network of founders who believe business can create meaningful change." },
    { icon: Handshake, title: "Access", d: "Warm introductions to incubators, accelerators, grants and other ecosystem partners." },
    { icon: Rocket, title: "Build", d: "Practical support and conversations to help founders navigate the decisions that matter." },
];

const BOOK_THEMES = [
    { t: "Origin Stories", d: "The moment, problem or conviction that started the journey." },
    { t: "Building the Team", d: "How founders attract people, create culture and build organizations that last." },
    { t: "Hard Trade-offs", d: "The moments when growth, capital, mission and survival pull in different directions." },
];

const NETWORK_FEATURES = [
    { t: "Curated peer conversations", d: "Talk honestly with founders facing similar decisions." },
    { t: "Founder-to-founder introductions", d: "Meet people who can help you think, build or grow." },
    { t: "Ecosystem introductions", d: "Get connected to relevant incubators, accelerators, grant programs and ecosystem partners." },
    { t: "Founder masterclasses", d: "Learn directly from people who have already navigated the hard parts." },
];

const ECO_CATS = [
    { icon: Rocket, t: "Incubators", d: "Programs aligned with your sector, stage and ambition." },
    { icon: Sparkles, t: "Grants", d: "Non-dilutive funding opportunities relevant to your work." },
    { icon: Landmark, t: "Accelerators", d: "Programs offering capital, mentorship and market access." },
    { icon: Handshake, t: "Ecosystem Partners", d: "Organizations and institutions that can help you move forward." },
];

const CONV_TAGS = ["Founder Stories", "Purpose & Profit", "Building Teams", "Hard Decisions", "Impact & Growth"];

export default function Home() {
    const [founders, setFounders] = useState([]);
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        api.get("/enterprises").then((r) => setFounders(r.data.slice(0, 6))).catch(() => {});
        api.get("/blog-posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    }, []);

    return (
        <main data-testid="home-page" className="text-white">
            <SEO
                title="Purposeful, Inc. | Building Businesses That Matter"
                description="Purposeful, Inc. brings together founders, ideas and relationships to help build businesses that matter. Explore the book, founder network and ecosystem."
                path="/"
                keywords="Purposeful Inc, building businesses that matter, founder network, impact founders, founder ecosystem, incubators, accelerators, grants, Chandrashekar Bhat"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "@id": "https://purposeful.inc/#webpage",
                    "url": "https://purposeful.inc/",
                    "name": "Purposeful, Inc. | Building Businesses That Matter",
                    "description": "An ecosystem for founders building businesses that matter. Explore the book, the founder network and the ecosystem.",
                    "isPartOf": { "@id": "https://purposeful.inc/#website" },
                    "about": { "@id": "https://purposeful.inc/#organization" },
                    "primaryImageOfPage": "https://purposeful.inc/assets/og-cover.jpg",
                }}
            />

            {/* 1. HERO */}
            <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden fingerprint-bg">
                <div className="container-x mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 fade-up">
                        <div className="overline mb-5">Purposeful, Inc. · Ecosystem for founders</div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight">
                            Building <span className="text-[#D4AF37]">businesses</span><br />
                            that <span className="italic text-[#FF8C00]">matter</span>.
                        </h1>
                        <p className="mt-7 max-w-xl text-lg text-zinc-300 leading-relaxed">
                            Purposeful, Inc. brings together the founders, ideas
                            and relationships shaping a more purposeful way to
                            build a business.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/join" data-testid="hero-join-cta" className="btn-gold">
                                Join Purposeful <ArrowRight size={16} />
                            </Link>
                            <Link to="/book" data-testid="hero-book-cta" className="btn-ghost-gold">
                                Explore the Book <ArrowRight size={16} />
                            </Link>
                        </div>
                        <p className="mt-6 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                            100 founders. One question.
                        </p>
                    </div>

                    <div className="lg:col-span-5 relative fade-up">
                        <div className="relative mx-auto max-w-md">
                            <img
                                src={ASSETS.bookCover}
                                alt="Purposeful, Inc. book cover"
                                data-testid="hero-book-cover"
                                loading="eager"
                                decoding="async"
                                fetchpriority="high"
                                className="relative w-full"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. WHAT IS PURPOSEFUL */}
            <section className="section-y border-t border-white/5">
                <div className="container-x mx-auto max-w-7xl">
                    <div className="max-w-3xl">
                        <div className="overline mb-4">What is Purposeful?</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            A growing ecosystem<br />
                            for <span className="text-[#D4AF37]">purposeful founders.</span>
                        </h2>
                        <div className="mt-6 space-y-4 text-zinc-400 text-lg leading-relaxed">
                            <p>Building a meaningful business is rarely a solo journey.</p>
                            <p>Purposeful brings together founders building businesses where people, purpose and profit have to work together.</p>
                            <p>Through founder stories, conversations, community and ecosystem connections, we're building a place where ambitious entrepreneurs can learn from one another, find the right people and discover opportunities to move their businesses forward.</p>
                        </div>
                    </div>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {PILLARS.map((p, i) => (
                            <div key={i} className="card-dark p-8" data-testid={`pillar-${p.title.toLowerCase()}`}>
                                <p.icon size={22} className="text-[#D4AF37]" />
                                <h3 className="font-display text-xl mt-5">{p.title}</h3>
                                <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. THE BOOK */}
            <section className="section-y border-t border-white/5">
                <div className="container-x mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <img src={ASSETS.bookCover} alt="Purposeful, Inc." loading="lazy" decoding="async" className="w-full max-w-md mx-auto" />
                    </div>
                    <div className="lg:col-span-6">
                        <div className="overline mb-4">The flagship project</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            100 founders.<br />
                            <span className="text-[#D4AF37]">100 perspectives</span> on building a business that matters.
                        </h2>
                        <div className="mt-6 space-y-4 text-zinc-400 leading-relaxed">
                            <p>Purposeful, Inc. is a book about the decisions behind meaningful businesses.</p>
                            <p>Over three years, Chandrashekar Bhat is speaking with 100 founders across sectors to understand the convictions, trade-offs and turning points that shaped their ventures.</p>
                            <p className="text-zinc-200">Not another collection of startup playbooks. A collection of founder decisions.</p>
                        </div>
                        <div className="mt-8 grid sm:grid-cols-3 gap-4">
                            {BOOK_THEMES.map((t) => (
                                <div key={t.t} className="rounded-xl border border-white/10 p-5" data-testid={`book-theme-${t.t.toLowerCase().replace(/\s/g, '-')}`}>
                                    <div className="font-display text-lg">{t.t}</div>
                                    <p className="text-zinc-500 text-xs mt-2 leading-relaxed">{t.d}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8">
                            <Link to="/book" data-testid="home-book-cta" className="btn-gold">
                                Explore the Book <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. MEET THE FOUNDERS */}
            <section className="section-y border-t border-white/5">
                <div className="container-x mx-auto max-w-7xl">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div className="max-w-3xl">
                        <div className="overline mb-4">Directory</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            A directory of<br />
                            founders in the <span className="text-[#D4AF37]">Purposeful ecosystem.</span>
                        </h2>
                        <p className="mt-5 text-zinc-400 leading-relaxed">
                            100 founders. 12+ sectors. Filter by sector, browse
                            by name.
                        </p>
                    </div>
                        {founders.length > 0 && (
                            <Link to="/directory" data-testid="home-founders-cta" className="text-[#D4AF37] font-mono uppercase text-xs tracking-widest hover:text-[#F0C84B] inline-flex items-center gap-2">
                                Browse the directory <ArrowRight size={14} />
                            </Link>
                        )}
                    </div>

                    {founders.length === 0 ? (
                        <div className="rounded-2xl border border-white/10 p-10 md:p-14 max-w-3xl">
                            <div className="overline mb-3">In progress</div>
                            <p className="text-zinc-300 text-lg leading-relaxed">
                                The first set of founder stories is being written.
                                Join Purposeful to be the first to read them.
                            </p>
                            <Link to="/join" className="btn-ghost-gold mt-6" data-testid="home-founders-join-cta">
                                Join Purposeful <ArrowRight size={14} />
                            </Link>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {founders.map((f) => {
                                const href = f.founder_linkedin || f.website;
                                const Wrap = href ? "a" : "div";
                                const wrapProps = href ? { href, target: "_blank", rel: "noreferrer" } : {};
                                return (
                                    <Wrap key={f.id} {...wrapProps} data-testid="home-founder-card" className="card-dark p-8 block group">
                                        <div className="flex items-start gap-4">
                                            {f.logo_url ? (
                                                <img src={f.logo_url} alt={f.startup_name} loading="lazy" className="h-14 w-14 rounded-full object-cover ring-1 ring-white/10" />
                                            ) : (
                                                <div className="h-14 w-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-display text-[#D4AF37] text-xl">
                                                    {f.founder_name?.[0]}
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <div className="font-display text-lg leading-tight">{f.founder_name}</div>
                                                <div className="text-zinc-500 text-xs mt-1 truncate">{f.startup_name} · {f.sector}</div>
                                            </div>
                                        </div>
                                        {f.impact_statement ? (
                                            <p className="mt-5 text-zinc-300 text-sm leading-relaxed italic">"{f.impact_statement}"</p>
                                        ) : (
                                            <p className="mt-5 text-zinc-400 text-sm leading-relaxed line-clamp-4">{f.description}</p>
                                        )}
                                        {href && (
                                    <div className="mt-5 text-[#D4AF37] font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 group-hover:text-[#F0C84B]">
                                                Read the profile <ArrowRight size={12} />
                                            </div>
                                        )}
                                    </Wrap>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* 5. NETWORK */}
            <section className="section-y section-forest">
                <div className="container-x mx-auto max-w-7xl">
                    <div className="max-w-2xl mb-12">
                        <div className="overline mb-4">The Purposeful Network</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            The room for founders<br />
                            building <span className="text-[#D4AF37]">businesses that matter.</span>
                        </h2>
                        <p className="mt-5 text-zinc-400 leading-relaxed">
                            A curated network of founders who believe that
                            building a great business and creating meaningful
                            impact aren't opposing goals.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {NETWORK_FEATURES.map((f, i) => (
                            <div key={i} className="card-dark p-7" data-testid={`network-highlight-${i}`}>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Feature</div>
                                <h3 className="font-display text-lg mt-3 leading-tight">{f.t}</h3>
                                <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{f.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-wrap items-center gap-6">
                        <p className="text-zinc-300 italic max-w-xl">
                            Purposeful isn't a directory. It's a network built on trust.
                        </p>
                        <Link to="/network" data-testid="home-network-cta" className="btn-gold">
                            Join the Network <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* 7. ECOSYSTEM */}
            <section className="section-y border-t border-white/5">
                <div className="container-x mx-auto max-w-7xl">
                    <div className="max-w-2xl mb-12">
                        <div className="overline mb-4">The Ecosystem</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            The right introduction can change<br />
                            the <span className="text-[#D4AF37]">trajectory of a business.</span>
                        </h2>
                        <p className="mt-5 text-zinc-400 leading-relaxed">
                            Founders don't just need information. They need
                            access to the right people at the right time.
                            Purposeful is building relationships with incubators,
                            accelerators, grant programs and other ecosystem
                            organizations so founders in the network can discover
                            relevant opportunities and make warm introductions
                            where there's a genuine fit.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {ECO_CATS.map((c, i) => (
                            <div key={i} className="card-dark p-7" data-testid={`eco-cat-${i}`}>
                                <c.icon size={20} className="text-[#D4AF37]" />
                                <h3 className="font-display text-lg mt-4">{c.t}</h3>
                                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{c.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10">
                        <Link to="/apply" data-testid="home-ecosystem-cta" className="btn-gold">
                            Apply for Ecosystem Access <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* 8. FOUNDER CONVERSATIONS */}
            <section className="section-y border-t border-white/5">
                <div className="container-x mx-auto max-w-7xl">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                        <div>
                            <div className="overline mb-4">Founder Stories</div>
                            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                                The book, written<br />
                                <span className="text-[#D4AF37]">one story at a time.</span>
                            </h2>
                            <p className="mt-5 text-zinc-400 max-w-xl leading-relaxed">
                                Read ideas, stories and lessons from the founders behind Purposeful.
                            </p>
                        </div>
                        {posts.length > 0 && (
                            <Link to="/founder-stories" data-testid="home-conv-cta" className="text-[#D4AF37] font-mono uppercase text-xs tracking-widest hover:text-[#F0C84B] inline-flex items-center gap-2">
                                Read the latest story <ArrowRight size={14} />
                            </Link>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-8">
                        {CONV_TAGS.map((t) => (
                            <span key={t} className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400" data-testid={`conv-tag-${t.toLowerCase().replace(/\s/g, '-')}`}>{t}</span>
                        ))}
                    </div>

                    {posts.length === 0 ? (
                        <div className="rounded-2xl border border-white/10 p-10 md:p-14 max-w-3xl">
                            <p className="text-zinc-300 text-lg leading-relaxed">
                                The first conversations are being edited for
                                publication. Join Purposeful to read them as they
                                go live.
                            </p>
                            <Link to="/join" className="btn-ghost-gold mt-6" data-testid="home-conv-join-cta">
                                Join Purposeful <ArrowRight size={14} />
                            </Link>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-3 gap-6">
                            {posts.map((p) => (
                                <a
                                    key={p.id}
                                    href={p.substack_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="home-conv-card"
                                    className="card-dark p-7 block"
                                >
                                    <div className="overline">{p.founder_name || "Conversation"}</div>
                                    <h3 className="font-display text-lg mt-3 leading-tight">{p.title}</h3>
                                    <p className="text-zinc-500 mt-3 text-sm line-clamp-3">{p.excerpt}</p>
                                    <div className="mt-5 text-[#D4AF37] text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2">
                                        Read the conversation <ExternalLink size={12} />
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* 9. ABOUT CHANDRASHEKAR */}
            <section className="section-y border-t border-white/5">
                <div className="container-x mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <img src={ASSETS.author} alt="Chandrashekar Bhat" loading="lazy" decoding="async" className="w-full max-w-md rounded-2xl ring-1 ring-white/10 object-cover" />
                    </div>
                    <div className="lg:col-span-7">
                        <div className="overline mb-4">About the founder</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
                            I'm building Purposeful,<br />
                            <span className="text-[#D4AF37]">one founder at a time.</span>
                        </h2>
                        <div className="mt-6 space-y-4 text-zinc-400 leading-relaxed">
                            <p>I'm Chandrashekar Bhat.</p>
                            <p>Over the past decade, I've worked alongside founders across climate, sustainability, health, education and emerging businesses.</p>
                            <p>I've repeatedly found myself asking the same question: what makes some founders build businesses that matter, even when the easier choice is to compromise?</p>
                            <p>Purposeful began as an attempt to answer that question. Today, I'm speaking with 100 founders, documenting what I learn and building a network around them.</p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/about" data-testid="home-about-cta" className="btn-gold">
                                Meet Chandrashekar <ArrowRight size={16} />
                            </Link>
                            <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className="btn-ghost-gold" data-testid="home-linkedin-cta">
                                <Linkedin size={14} /> Follow on LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* 10. COMMUNITY / EMAIL CTA */}
            <section className="section-y border-t border-white/5 relative overflow-hidden">
                <div className="absolute inset-0 fingerprint-bg" />
                <div className="container-x mx-auto max-w-3xl text-center relative">
                    <div className="overline mb-4">The Purposeful email</div>
                    <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight">
                        Stay close to<br />
                        <span className="text-[#D4AF37]">Purposeful.</span>
                    </h2>
                    <p className="mt-6 text-zinc-400 text-lg">
                        Get the best ideas from the founders we're speaking with,
                        new founder stories and occasional opportunities from
                        the Purposeful ecosystem.
                    </p>
                    <div className="mt-8 max-w-lg mx-auto">
                        <WaitlistForm source="home-community-cta" testIdPrefix="home-community-waitlist" compact />
                    </div>
                    <p className="mt-4 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                        One useful email a month · No noise · Unsubscribe anytime
                    </p>
                </div>
            </section>

            {/* 11. FINAL CTA */}
            <section className="section-y section-forest">
                <div className="container-x mx-auto max-w-4xl text-center">
                    <div className="overline mb-4">The invitation</div>
                    <h2 className="font-display text-5xl md:text-7xl font-semibold tracking-tight leading-[1.02]">
                        Build something<br />
                        that <span className="text-[#D4AF37]">matters.</span>
                    </h2>
                    <p className="mt-8 text-zinc-300 text-lg max-w-2xl mx-auto">
                        Purposeful is for founders who believe business can be
                        ambitious, sustainable and meaningful at the same time.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link to="/join" data-testid="final-join-cta" className="btn-gold">Join Purposeful <ArrowRight size={16} /></Link>
                        <Link to="/apply" data-testid="final-apply-cta" className="btn-ghost-gold">Apply for Ecosystem Access <ArrowRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
